import React from 'react';
import { arrayOf, string, func, shape, number, bool, oneOfType, instanceOf } from 'prop-types';
import { connect } from 'react-redux';
import { getDefaultKeyBinding, RichUtils, getVisibleSelectionRect } from 'draft-js';
import createLinkifyPlugin from 'draft-js-linkify-plugin';
import Editor from 'draft-js-plugins-editor';
import omit from 'lodash/omit';
import uniqBy from 'lodash/uniqBy';
import isEqual from 'lodash/isEqual';
import classnames from 'classnames';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import { getWorkspaceMembersMentionsByWorkspaceId, getWorkspaceType } from 'Shared/selectors/common';
import { getClientSideSearchData } from 'Shared/actions/clientSideSearchData';
import ColorConstants from 'Wpds/colors/ColorConstants';

import { smartLinksPlugin } from './plugins/smartLinksPlugin';
import { imageAttachmentPlugin } from './plugins/imageAttachmentsPlugin';
import { mentionPluginCreator, addMention } from './plugins/mentionPluginCreator';
import { applyLink, addLinkPlugin } from './plugins/addLinkPlugin';
import { emojiPlugin } from './plugins/addEmojiPlugin';
import { emojiMentionPluginCreator } from './plugins/emojiMentionPlugin';
import { suggestionsFilter, SECTION_HEADER_TITLE_TYPE } from './utils';

import AddLinkInputTooltip from './components/AddLinkInputTooltip/AddLinkInputTooltip';
import MentionDropdown from './components/MentionDropdown/MentionDropdown';
import EmojiSuggestions from './components/EmojiSuggestions/EmojiSuggestions';
import EntryComponent from './components/EntryComponent';
import GifBlock from './components/GifBlock/GifBlock';

import Toolbar from './Toolbar';

import './DraftEditor.scss';

const styleMap = {
  HIGHLIGHT: {
    backgroundColor: '#d7e8f9',
  },
  GRAY: {
    color: ColorConstants.gray[800],
  },
};

const everyoneOption = { id: 0, isEveryone: true, mentionEntryName: 'Everyone', name: '', isWorkspaceMember: true };

function blockStyleFn(contentBlock) {
  const type = contentBlock.getType();
  if (type === SECTION_HEADER_TITLE_TYPE) {
    return SECTION_HEADER_TITLE_TYPE;
  }
  return null;
}

function blockRendererFn(contentBlock) {
  const type = contentBlock.getType();
  if (type === 'gif-block') {
    return {
      component: GifBlock,
      editable: false,
    };
  }
  return null;
}

class DraftEditor extends React.Component {
  static propTypes = {
    placeholder: string,
    className: string,
    meetingItemUuid: string,
    onChange: func,
    onFocus: func,
    onBlur: func,
    mentions: arrayOf(
      shape({
        id: number.isRequired,
        label: string.isRequired,
        global_id: string.isRequired,
      })
    ),
    workspaceMembersMentions: arrayOf(
      shape({
        id: number.isRequired,
        global_id: string.isRequired,
      })
    ),
    isGroupWorkspace: bool,
    isInlineAttachments: bool,
    imageAttachmentFullWidth: bool,
    onKeyCommandActionsMap: func,
    handleImageAttachmentRemove: func,
    dispatch: func.isRequired,
    editorState: shape({}).isRequired,
    editorRef: oneOfType([func, shape({ current: instanceOf(Element) }), shape({ current: null })]),
    showStyleToolbar: bool,
    workspaceId: string,
  };

  static displayName = 'WrapperDraftEditor';

  static defaultProps = {
    meetingItemUuid: null,
    editorRef: { current: null },
    mentions: [],
    workspaceMembersMentions: [],
    isGroupWorkspace: false,
    placeholder: '',
    isInlineAttachments: false,
    imageAttachmentFullWidth: false,
    className: '',
    handleImageAttachmentRemove: undefined,
    onKeyCommandActionsMap: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
    onBlur: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
    onFocus: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
    onChange: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
    showStyleToolbar: false,
    workspaceId: null,
  };

  state = {
    assignessListState: this.assignessList,
    isShowAddLinkInput: false,
    suggestionsListOpen: false,
  };

  editorStateWithoutMention = this.props.editorState;

  assignedPlugin = mentionPluginCreator({});

  emojiMentionPlugin = emojiMentionPluginCreator();

  linkifyPlugin = createLinkifyPlugin({
    target: '_blank',
    component: ({ className, href, rel, target, children }) => (
      <a
        className={`font-weight-bold ${className}`}
        href={href}
        style={{ color: ColorConstants.purple[800] }}
        rel={rel}
        target={target}
        onClick={event => {
          event.preventDefault();
          window.open(href, '_blank', 'noopener,noreferrer');
        }}
      >
        {children}
      </a>
    ),
  });

  get assignessList() {
    const { mentions, workspaceMembersMentions, isGroupWorkspace } = this.props;
    const additionalOptions = isGroupWorkspace ? [everyoneOption] : [];

    return uniqBy(
      [
        ...additionalOptions,
        ...workspaceMembersMentions,
        ...mentions.filter(mention => mention.in_same_org || mention.collaborator).map(m => omit(m, 'avatar')),
      ],
      'id'
    );
  }

  // Don't re-render if mention lists are the same
  // shouldComponentUpdate = (nextProps, nextState) => {
  //   console.log("is equal?", !isEqual(this.props, nextProps));
  //   return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  // };

  handleSearchAssignesChange = async ({ value }) => {
    const { dispatch } = this.props;
    await dispatch(getClientSideSearchData());
    const workspaceMembersMentions = this.assignessList.filter(({ isWorkspaceMember }) => isWorkspaceMember);
    if (!value.length) return this.setState({ assignessListState: workspaceMembersMentions });

    const filteredWorkspaceMembersMentions = suggestionsFilter(value, workspaceMembersMentions);
    if (filteredWorkspaceMembersMentions.length)
      return this.setState({ assignessListState: filteredWorkspaceMembersMentions });

    return this.setState({ assignessListState: suggestionsFilter(value, this.assignessList) });
  };

  // These key bindings are managed by DraftJS.
  keyBindingFn = event => {
    if (event.key === 'Tab') {
      const newEditorState = RichUtils.onTab(event, this.props.editorState, 2 /* maxDepth */);
      if (newEditorState !== this.props.editorState) {
        this.props.onChange(newEditorState);
      }
      return null;
    }
    if (event.keyCode === 69 && (event.ctrlKey || event.metaKey)) return 'highlight';
    if (event.key === 'Enter' && event.keyCode === 13 && event.ctrlKey) return 'ctrl+enter';
    if (event.key === 'Enter' && event.keyCode === 13 && !event.shiftKey) return 'save-block';
    if (event.keyCode === 75 && (event.ctrlKey || event.metaKey)) return 'add-link';

    return getDefaultKeyBinding(event);
  };

  handleAddLinkOpen = () => {
    if (!getVisibleSelectionRect(window)) return;
    this.setState({ isShowAddLinkInput: true });
  };

  handleLinkAdd = url => {
    const { editorState, onChange } = this.props;
    onChange(applyLink({ editorState, url }));
    this.setState({ isShowAddLinkInput: false });
  };

  handleKeyCommand = (command, editorState) => {
    const { onKeyCommandActionsMap, onChange } = this.props;
    const commonKeyComandActionsMap = {
      underline: () => {
        onChange(RichUtils.handleKeyCommand(editorState, command));
        return 'handled';
      },
      italic: () => {
        onChange(RichUtils.handleKeyCommand(editorState, command));
        return 'handled';
      },
      bold: () => {
        onChange(RichUtils.handleKeyCommand(editorState, command));
        return 'handled';
      },
      highlight: () => {
        onChange(RichUtils.toggleInlineStyle(editorState, 'HIGHLIGHT'));
        return 'handled';
      },
      'add-link': () => this.handleAddLinkOpen(),
    };
    const actionsMap = { ...commonKeyComandActionsMap, ...onKeyCommandActionsMap() };
    const actionFunction = actionsMap[command];
    return actionFunction ? actionFunction() : 'not-handled';
  };

  handleAddMention = value => {
    const { onChange, workspaceMembersMentions } = this.props;
    if (value.isEveryone) {
      const newEditorState = workspaceMembersMentions
        .filter(({ isCurrentUser }) => !isCurrentUser)
        .reduce(
          (editorState, mention, index) => addMention({ editorState, mention, isFirst: !index }),
          this.editorStateWithoutMention
        );
      setTimeout(() => onChange(newEditorState), 0);
    }
  };

  handleSuggestionsListOpen = () => {
    const { editorState } = this.props;
    this.editorStateWithoutMention = editorState;
    this.setState({ suggestionsListOpen: true });
  };

  handleSuggestionsListClose = () => this.setState({ suggestionsListOpen: false });

  handleEditorWrapperKeyDown = event => this.state.suggestionsListOpen && event.stopPropagation();

  shouldHidePlaceholder = () => {
    // If the user changes block type before entering any text,
    // we hide the the placeholder text
    const { editorState } = this.props;
    const contentState = editorState.getCurrentContent();
    if (!contentState.hasText()) {
      if (contentState.getBlockMap().first().getType() !== 'unstyled') {
        return true;
      }
    }
    return false;
  };

  render() {
    const {
      editorRef,
      isInlineAttachments,
      handleImageAttachmentRemove,
      imageAttachmentFullWidth,
      className,
      showStyleToolbar,
      flags,
      ...props
    } = this.props;

    const { draftEditorStyleToolbar: showStyleToolbarFlag } = flags;
    const { assignessListState, isShowAddLinkInput } = this.state;
    const predefinedPlugins = [
      this.assignedPlugin,
      emojiPlugin,
      smartLinksPlugin,
      addLinkPlugin,
      this.linkifyPlugin,
      this.emojiMentionPlugin,
    ];
    const plugins = isInlineAttachments
      ? [...predefinedPlugins, imageAttachmentPlugin({ handleImageAttachmentRemove, imageAttachmentFullWidth })]
      : [...predefinedPlugins];
    const AssigneesSuggestions = this.assignedPlugin.MentionSuggestions;

    const baseClassName = 'WP-DraftJSEditor';
    const classes = classnames(
      className,
      baseClassName,
      this.shouldHidePlaceholder() && `${baseClassName}--hide-placeholder`
    );

    return (
      <div className={classes} onKeyDown={this.handleEditorWrapperKeyDown}>
        <EmojiSuggestions
          emojiMentionPlugin={this.emojiMentionPlugin}
          onOpen={this.handleSuggestionsListOpen}
          onClose={this.handleSuggestionsListClose}
        />
        <AssigneesSuggestions
          suggestions={assignessListState}
          onAddMention={this.handleAddMention}
          onSearchChange={this.handleSearchAssignesChange}
          onOpen={this.handleSuggestionsListOpen}
          onClose={this.handleSuggestionsListClose}
          popoverComponent={<MentionDropdown />}
          entryComponent={EntryComponent}
        />
        {showStyleToolbar && showStyleToolbarFlag && (
          <div className="mb-2">
            <Toolbar editorState={this.props.editorState} onChange={this.props.onChange} />
          </div>
        )}
        <Editor
          {...props}
          spellCheck
          ref={editorRef}
          plugins={plugins}
          handleKeyCommand={this.handleKeyCommand}
          keyBindingFn={this.keyBindingFn}
          customStyleMap={styleMap}
          blockRendererFn={blockRendererFn}
          blockStyleFn={blockStyleFn}
          onTab={this.keyBindingFn}
        />
        {isShowAddLinkInput && (
          <AddLinkInputTooltip
            editorRef={editorRef}
            onClose={this.handleAddLinkToggle}
            onLinkAdd={this.handleLinkAdd}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  workspaceMembersMentions: getWorkspaceMembersMentionsByWorkspaceId(ownProps?.workspaceId)(state),
  isGroupWorkspace: getWorkspaceType(ownProps.meetingItemUuid)(state) === 'is_group',
  mentions: state.clientSideSearchData.data,
});

const mapDispatchToProps = dispatch => ({ dispatch });

// If the client side search data doesn't change or the workspaces don't change
// then don't bother re-rendering because the global state changed.
const areStatesEqual = (next, prev) => {
  return (
    isEqual(prev.clientSideSearchData?.data?.length, next.clientSideSearchData?.data?.length) &&
    isEqual(prev.workspace?.users?.length, next.workspace?.users?.length) &&
    isEqual(prev.workspaces?.length, next.workspaces?.length)
  );
};

export default connect(mapStateToProps, mapDispatchToProps, null, { pure: true, areStatesEqual })(
  withLDConsumer()(DraftEditor)
);
